import React from 'react'
import { Helmet } from 'react-helmet-async'

/**
 * ProductSchema - Generates structured data for products
 * Helps with SEO and Google Shopping integration
 *
 * @param {Object} props
 * @param {Array} props.products - Array of product objects
 * @param {boolean} props.isCAD - Whether prices are in CAD
 */
export function ProductSchema({ products, isCAD = false }) {
    // Filter to only in-stock products
    const inStockProducts = products.filter((product) => product.onHandQty > 0)

    // Create ItemList schema for the shop page
    const itemListSchema = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: inStockProducts.map((product, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
                '@type': 'Product',
                name: product.descriptionTitle,
                description: product.description,
                image: `https://electrobraid.com/shop/images/cards/${product.href}.jpg`,
                sku: product.productID,
                brand: {
                    '@type': 'Brand',
                    name: 'ElectroBraid',
                },
                offers: {
                    '@type': 'Offer',
                    url: `https://electrobraid.com/shop#${product.href}`,
                    priceCurrency: isCAD ? 'CAD' : 'USD',
                    price: product.srp,
                    availability: 'https://schema.org/InStock',
                    priceValidUntil: '2026-12-31',
                    seller: {
                        '@type': 'Organization',
                        name: 'ElectroBraid',
                    },
                    // Merchant Listing: 30-day return policy
                    hasMerchantReturnPolicy: {
                        '@type': 'MerchantReturnPolicy',
                        applicableCountry: ['US', 'CA'],
                        returnPolicyCategory:
                            'https://schema.org/MerchantReturnFiniteReturnWindow',
                        merchantReturnDays: 30,
                        returnMethod: 'https://schema.org/ReturnByMail',
                        returnFees: 'https://schema.org/ReturnShippingFees',
                    },
                    // Merchant Listing: Shipping (calculated at checkout)
                    shippingDetails: [
                        {
                            '@type': 'OfferShippingDetails',
                            shippingDestination: {
                                '@type': 'DefinedRegion',
                                addressCountry: 'US',
                            },
                            deliveryTime: {
                                '@type': 'ShippingDeliveryTime',
                                handlingTime: {
                                    '@type': 'QuantitativeValue',
                                    minValue: 1,
                                    maxValue: 2,
                                    unitCode: 'DAY',
                                },
                                transitTime: {
                                    '@type': 'QuantitativeValue',
                                    minValue: 2,
                                    maxValue: 5,
                                    unitCode: 'DAY',
                                },
                            },
                            // Shipping rate is calculated at checkout,
                            // so shippingRate is intentionally omitted.
                        },
                        {
                            '@type': 'OfferShippingDetails',
                            shippingDestination: {
                                '@type': 'DefinedRegion',
                                addressCountry: 'CA',
                            },
                            deliveryTime: {
                                '@type': 'ShippingDeliveryTime',
                                handlingTime: {
                                    '@type': 'QuantitativeValue',
                                    minValue: 1,
                                    maxValue: 2,
                                    unitCode: 'DAY',
                                },
                                transitTime: {
                                    '@type': 'QuantitativeValue',
                                    minValue: 2,
                                    maxValue: 6,
                                    unitCode: 'DAY',
                                },
                            },
                        },
                    ],
                },
            },
        })),
    }

    return (
        <Helmet>
            <script type="application/ld+json">
                {JSON.stringify(itemListSchema)}
            </script>
        </Helmet>
    )
}

/**
 * OrganizationSchema - Business information structured data
 * Should be on every page
 */

export function OrganizationSchema() {
    const orgSchema = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'ElectroBraid',
        url: 'https://electrobraid.com',
        logo: 'https://electrobraid.com/images/logo/logo.png',
        description:
            "America's #1 Electric Horse Fence - Safe, durable electric fencing solutions since 1998",
        foundingDate: '1998',
        address: {
            '@type': 'PostalAddress',
            addressCountry: 'CA',
        },
        email: 'info@electrobraid.com',
        contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+1-855-323-3623',
            contactType: 'Customer Service',
            areaServed: ['US', 'CA'],
            availableLanguage: ['English'],
        },
        sameAs: [
            'https://www.facebook.com/electrobraid',
            'https://www.instagram.com/electrobraid.fence',
        ],
    }

    return (
        <Helmet>
            <script type="application/ld+json">
                {JSON.stringify(orgSchema)}
            </script>
        </Helmet>
    )
}

export default ProductSchema
